import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, Mail, Shield, Truck } from 'lucide-react'
import OnboardingLayout from '../components/onboarding/OnboardingLayout'
import AccountLoadError from '../components/AccountLoadError'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import {
  legacyRoleToLabel,
  getEntryContext,
  entryDestination,
  optionForNext,
  resolvePostEntryPath,
  safeNextPath,
  setEntryContext,
} from '../lib/entryContext'

interface AvailableCompany {
  id: string
  name: string
  regCode?: string
  role: string
}

type EntrySelection =
  | { kind: 'company'; companyId: string; previewRole: string }
  | { kind: 'platform'; previewRole: 'Admin' }
  | { kind: 'courier'; previewRole: 'Courier' }
  | { kind: 'partner'; previewRole: 'Partner' }
  | { kind: 'apply'; previewRole: 'Merchant' }

function selectionKey(selection: EntrySelection): string {
  if (selection.kind === 'company') return `company:${selection.companyId}:${selection.previewRole}`
  return selection.kind
}

export default function EntryPage() {
  const navigate = useNavigate()
  // The page the user opened before being sent here; ProtectedRoute passes it on.
  const [searchParams] = useSearchParams()
  const next = safeNextPath(searchParams.get('next'))
  const {
    loading: roleLoading,
    accountLoadError,
    retryAccountLoad,
    companyId,
    isPlatformAdmin,
    isCourier,
    isCourierPartner,
    partnerCompany,
    companyApprovalStatus,
    switchCompany,
    setPreviewRole,
  } = useAuth()
  const [selected, setSelected] = useState<EntrySelection | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
    isFetching: companiesFetching,
    refetch: refetchCompanies,
  } = useQuery({
    queryKey: ['entry', 'companies'],
    queryFn: async () => {
      const res = await api.get<AvailableCompany[]>('/me/companies')
      return res.data ?? []
    },
    enabled: !roleLoading,
  })

  const options = useMemo((): EntrySelection[] => {
    const items: EntrySelection[] = companies.map((company) => ({
      kind: 'company',
      companyId: company.id,
      previewRole: legacyRoleToLabel(company.role),
    }))

    if (isPlatformAdmin) {
      items.unshift({ kind: 'platform', previewRole: 'Admin' })
    }

    if (isCourier) {
      items.push({ kind: 'courier', previewRole: 'Courier' })
    }

    if (isCourierPartner) {
      items.push({ kind: 'partner', previewRole: 'Partner' })
    }

    if (companies.length === 0 && !isCourier && !isCourierPartner && !isPlatformAdmin) {
      items.push({ kind: 'apply', previewRole: 'Merchant' })
    }

    return items
  }, [companies, isPlatformAdmin, isCourier, isCourierPartner])

  useEffect(() => {
    if (roleLoading || companiesLoading || submitting || accountLoadError) return

    const ctx = getEntryContext()
    if (!ctx) return

    const enteringPlatform =
      isPlatformAdmin && (ctx.isPlatformAdmin === true || !ctx.companyId)
    const destination = entryDestination(
      resolvePostEntryPath({
        isPlatformAdmin: enteringPlatform,
        companyApprovalStatus,
        previewRole: enteringPlatform ? 'Admin' : ctx.previewRole,
        isCourier,
        isCourierPartner,
      }),
      next,
    )
    navigate(destination, { replace: true })
  }, [
    roleLoading,
    companiesLoading,
    submitting,
    accountLoadError,
    isPlatformAdmin,
    isCourier,
    isCourierPartner,
    companyApprovalStatus,
    next,
    navigate,
  ])

  useEffect(() => {
    if (options.length === 1 && !selected) {
      setSelected(options[0])
    }
  }, [options, selected])

  const continueDisabled = !selected || submitting || roleLoading || companiesLoading

  const handleContinue = async (choice: EntrySelection | null = selected) => {
    if (!choice) return

    setSubmitting(true)
    setError(null)

    try {
      const targetCompanyId = choice.kind === 'company' ? choice.companyId : null

      setEntryContext({
        companyId: targetCompanyId,
        previewRole: choice.previewRole,
        isPlatformAdmin: choice.kind === 'platform' || (isPlatformAdmin && choice.previewRole === 'Admin'),
        isCourier: choice.kind === 'courier' || isCourier,
        isCourierPartner: choice.kind === 'partner' || isCourierPartner,
      })

      if (choice.previewRole) {
        setPreviewRole(choice.previewRole)
      }

      // The page reloads in the new company and the effect above takes it on to `next`.
      if (choice.kind === 'company' && targetCompanyId && targetCompanyId !== companyId) {
        await switchCompany(targetCompanyId)
        return
      }

      const destination = entryDestination(
        resolvePostEntryPath({
          isPlatformAdmin: choice.kind === 'platform',
          companyApprovalStatus,
          previewRole: choice.previewRole,
          isCourier: choice.kind === 'courier' || isCourier,
          isCourierPartner: choice.kind === 'partner' || isCourierPartner,
        }),
        next,
      )
      navigate(destination, { replace: true })
    } catch (err) {
      console.error('Failed to enter backoffice context', err)
      setError('Could not switch to the selected company. Please try again.')
      setSubmitting(false)
    }
  }

  // With a single option there is nothing to choose, and a courier, partner or
  // platform link (a FiveStar job e-mail) names its own: continue straight away, once.
  const ready = !roleLoading && !companiesLoading && !accountLoadError && !companiesError
  const onlyOption = ready && options.length === 1 ? options[0] : null
  const autoOption = ready ? (onlyOption ?? optionForNext(options, next)) : null
  const autoContinued = useRef(false)
  useEffect(() => {
    if (!autoOption || autoContinued.current || getEntryContext()) return
    autoContinued.current = true
    // Chosen on screen too, so a failed switch leaves it ready for Continue.
    setSelected(autoOption)
    void handleContinue(autoOption)
  }, [autoOption])

  // A failed load is not "no companies": show the error with Retry, not only "Apply to join".
  if (accountLoadError || companiesError) {
    return (
      <AccountLoadError
        detail={accountLoadError ?? companiesError?.message}
        retrying={Boolean(companiesError) && companiesFetching}
        onRetry={() => {
          if (accountLoadError) retryAccountLoad()
          if (companiesError) void refetchCompanies()
        }}
      />
    )
  }

  if (roleLoading || companiesLoading || (autoOption && !error)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        Loading...
      </div>
    )
  }

  return (
    <OnboardingLayout>
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Choose company and role
          </h1>
          <p className="text-sm text-foreground-muted">
            Select how you want to use the backoffice. You can switch company context later from the
            header.
          </p>
        </div>

        <div className="space-y-3">
          {options.map((option) => {
            const key = selectionKey(option)
            const isActive = selected ? selectionKey(selected) === key : false

            if (option.kind === 'platform') {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(option)}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    isActive
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-surface hover:bg-surface-hover'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Shield className="mt-0.5 h-5 w-5 text-accent shrink-0" />
                    <div>
                      <p className="font-semibold">Platform admin</p>
                      <p className="text-sm text-foreground-muted mt-1">
                        Manage companies, couriers, and system settings across ComfortHub.
                      </p>
                      <p className="text-xs text-accent mt-2 font-semibold">Role: Admin</p>
                    </div>
                  </div>
                </button>
              )
            }

            if (option.kind === 'courier') {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(option)}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    isActive
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-surface hover:bg-surface-hover'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Truck className="mt-0.5 h-5 w-5 text-accent shrink-0" />
                    <div>
                      <p className="font-semibold">Courier deliveries</p>
                      <p className="text-sm text-foreground-muted mt-1">
                        View orders assigned to you for delivery.
                      </p>
                      <p className="text-xs text-accent mt-2 font-semibold">Role: Courier</p>
                    </div>
                  </div>
                </button>
              )
            }

            if (option.kind === 'partner') {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(option)}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    isActive
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-surface hover:bg-surface-hover'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 text-accent shrink-0" />
                    <div>
                      <p className="font-semibold">{partnerCompany?.displayName || 'Partner'} desk</p>
                      <p className="text-sm text-foreground-muted mt-1">
                        Same zone map and cost matrix, plus active and history deliveries you can
                        update.
                      </p>
                      <p className="text-xs text-accent mt-2 font-semibold">
                        Role: {partnerCompany?.displayName || 'Partner'}
                      </p>
                    </div>
                  </div>
                </button>
              )
            }

            if (option.kind === 'apply') {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(option)}
                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                    isActive
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-surface hover:bg-surface-hover'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Building2 className="mt-0.5 h-5 w-5 text-accent shrink-0" />
                    <div>
                      <p className="font-semibold">Apply to join ComfortHub</p>
                      <p className="text-sm text-foreground-muted mt-1">
                        Start a new merchant application for platform review.
                      </p>
                      <p className="text-xs text-accent mt-2 font-semibold">Role: Merchant</p>
                    </div>
                  </div>
                </button>
              )
            }

            const company = companies.find((c) => c.id === option.companyId)
            if (!company) return null

            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelected(option)}
                className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                  isActive
                    ? 'border-accent bg-accent/10'
                    : 'border-border bg-surface hover:bg-surface-hover'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Building2 className="mt-0.5 h-5 w-5 text-accent shrink-0" />
                  <div>
                    <p className="font-semibold">{company.name}</p>
                    {company.regCode && (
                      <p className="text-xs text-foreground-muted mt-0.5">Reg: {company.regCode}</p>
                    )}
                    <p className="text-xs text-accent mt-2 font-semibold">
                      Role: {option.previewRole}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {error && <p role="alert" className="text-sm text-red-400">{error}</p>}

        <button
          type="button"
          disabled={continueDisabled}
          onClick={() => void handleContinue()}
          className="w-full rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-strong transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Continuing…' : 'Continue'}
        </button>
      </div>
    </OnboardingLayout>
  )
}
